import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  computed,
  OnInit,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AiCreatePostModalService } from '@features/posts/services/ai-create-post-modal.service';
import { StepChoiceComponent } from './steps/step-choice/step-choice.component';
import { StepBusinessInfoComponent } from './steps/step-business-info/step-business-info.component';
import { StepGeneratingComponent } from './steps/step-generating/step-generating.component';
import { StepReviewComponent } from './steps/step-review/step-review.component';
import { StepSuccessComponent } from './steps/step-success/step-success.component';

@Component({
  selector: 'app-create-post-modal',
  standalone: true,
  imports: [
    StepChoiceComponent,
    StepBusinessInfoComponent,
    StepGeneratingComponent,
    StepReviewComponent,
    StepSuccessComponent,
  ],
  templateUrl: './create-post-modal.component.html',
  styleUrl: './create-post-modal.component.scss',
})
export class CreatePostModalComponent implements OnInit {
  protected readonly createPostService = inject(AiCreatePostModalService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly step = this.createPostService.currentStep;
  protected readonly isOpen = this.createPostService.isModalOpen;

  constructor() {
    effect(() => {
      const open = this.isOpen();
      document.documentElement.style.overflow = open ? 'hidden' : '';
      document.body.style.overflow = open ? 'hidden' : '';
    });

    this.destroyRef.onDestroy(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.isOpen()) {
          this.createPostService.closeModal();
        }
      });
  }

  protected modalTitle = computed(() => {
    const s = this.step();
    const titles: Record<number, string> = {
      0: 'Create a new post',
      1: 'Tell us about your business',
      3: 'Generating your posts',
      4: 'Review your posts',
    };
    return titles[s] ?? 'Create a new post';
  });

  protected modalSubtitle = computed(() => {
    const s = this.step();
    const subtitles: Record<number, string> = {
      0: 'Choose how you want to create',
      1: "We'll use this to generate relevant content",
      3: 'This usually takes about 30 seconds',
      4: 'Choose delivery, accounts, and posts',
    };
    return subtitles[s] ?? '';
  });

  closeModal() {
    this.createPostService.closeModal();
  }

  /** ESC does not close the modal — only dismisses an open post preview. */
  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen() && this.createPostService.isPreviewOpen()) {
      this.createPostService.closePreview();
    }
  }
}
